import uniqBy from 'lodash/uniqBy';
import camelCase from 'lodash/camelCase';

import { addPrimaryPlanToCommissionChanges } from 'bundles/apex/deals/form/store/deal/broker_commission_helpers';
import { deleteTaskAttachment } from 'bundles/apex/deals/show/store/tasks/actions';
import { findEmployerRole } from 'bundles/backoffice/contacts/contact_role_employment';
import { get, post, put } from 'helpers/api';
import { hasAttachment } from 'bundles/apex/tasks/helpers';
import { hasMatchingPayee } from 'bundles/backoffice/vouchers/helpers/commissions';
import {
  newTransactionSideDetails,
  setTransactionSideToDual
} from 'bundles/backoffice/vouchers/helpers/transaction_side_details';
import { serializeCommissions } from 'bundles/apex/deals/form/store/deal/serializer';

export function loadDeal(path) {
  return async (dispatch, getState) => {
    const deal = await get(path || getState().paths.deal);
    dispatch(setDeal(deal));
  };
}

export const SET_DEAL = 'SET_DEAL';
export function setDeal(deal) {
  return {
    type: SET_DEAL,
    deal,
  };
}

export const CHANGE_DEAL = 'CHANGE_DEAL';
export function changeDeal(changes) {
  return {
    type: CHANGE_DEAL,
    changes,
  };
}


export const ADD_CRITICAL_DATE = 'ADD_CRITICAL_DATE';
export function addCriticalDate(name, date) {
  return async (dispatch, getState) => {
    const createdDate = await post(getState().deal.datesPath, {
      name,
      date_attributes: date,
    });

    dispatch({ type: ADD_CRITICAL_DATE, date: createdDate, name: camelCase(createdDate.adHocName) });
  };
}

export function setCriticalDate(name, date) {
  return async (dispatch, getState) => {
    const deal = await put(getState().deal.path, { [name]: date });
    dispatch(setDeal(deal));
  };
}

export function deleteCriticalDate(name) {
  return async (dispatch, getState) => {
    const criticalDate = getState().deal[name];
    const dateForRequest = {
      ...criticalDate,
      date: null,
      relativeDateId: null,
      static: true
    };
    dispatch(setCriticalDate(name, dateForRequest));
  };
}

export const ADD_ATTACHMENT = 'ADD_ATTACHMENT';
export function addAttachment(attachment) {
  return {
    type: ADD_ATTACHMENT,
    attachment
  };
}

export const DELETE_ATTACHMENT = 'DELETE_ATTACHMENT';
export function deleteAttachment(attachment) {
  return (dispatch, getState) => {
    dispatch({ type: DELETE_ATTACHMENT, attachment });
    for (const task of getState().tasks) {
      if (hasAttachment(task, attachment)) {
        dispatch(deleteTaskAttachment(task, attachment));
      }
    }
  };
}


export const SET_ATTACHMENTS = 'SET_ATTACHMENTS';
export function setAttachments(attachments) {
  return {
    type: SET_ATTACHMENTS,
    attachments,
  };
}

export const SET_ATTACHMENT_FOLDERS = 'SET_ATTACHMENT_FOLDERS';
export function setAttachmentFolders(attachmentFolders) {
  return {
    type: SET_ATTACHMENT_FOLDERS,
    attachmentFolders,
  };
}

function saveContacts(state) {
  const contactRoles = uniqBy((state.deal.contactRoles || []), ({ contact, relationship, role }) => {
    return contact.id + contact.contactType + (role || relationship);
  });

  return put(state.deal.path, {
    contactRolesAttributes: contactRoles.map((contactRole) => {
      return {
        id: contactRole.id,
        contactId: contactRole.contact.id,
        crmContactId: contactRole.contact.crmContactId,
        crmContactType: contactRole.contact.crmContactType,
        employerContactId: contactRole.employer?.id,
        employerCrmContactId: contactRole.employer?.crmContactId,
        employerCrmContactType: contactRole.employer?.crmContactType,
        role: contactRole.role || contactRole.relationship,
        sharedAttributes: contactRole.sharedAttributes,
        _destroy: contactRole._destroy
      };
    })
  });
}

export const SET_CONTACT_ROLES = 'SET_CONTACT_ROLES';
function setContactRoles(contactRoles) {
  return {
    type: SET_CONTACT_ROLES,
    contactRoles
  };
}

export const ADD_CONTACT_ROLE = 'ADD_CONTACT_ROLE';
export function addContactRole(contactRole) {
  return async (dispatch, getState) => {
    if (contactRole.employer) {
      const employerRole = findEmployerRole(contactRole.employer, contactRole.role, getState().deal.contactRoles);
      if (!employerRole) {
        dispatch({
          type: ADD_CONTACT_ROLE,
          contactRole: {
            ...contactRole,
            contact: contactRole.employer,
            employer: null,
            sharedAttributes: contactRole.employer.sharedAttributes,
          },
        });
      }
    }

    dispatch({
      type: ADD_CONTACT_ROLE,
      contactRole,
    });

    const { contactRoles } = await saveContacts(getState());
    dispatch(setContactRoles(contactRoles));
  };
}

export const ADD_CONTACT_ROLES = 'ADD_CONTACT_ROLES';
export function addContactRoles(contactRoles) {
  return async (dispatch, getState) => {
    dispatch({
      type: ADD_CONTACT_ROLES,
      contactRoles,
    });

    const { contactRoles: savedContactRoles } = await saveContacts(getState());
    dispatch(setContactRoles(savedContactRoles));
  };
}

export const CHANGE_CONTACT_ROLE = 'CHANGE_CONTACT_ROLE';
export function changeContactRole(contactRole, changes) {
  return async (dispatch, getState) => {
    dispatch({
      type: CHANGE_CONTACT_ROLE,
      contactRole,
      changes,
    });

    const { contactRoles } = await saveContacts(getState());
    dispatch(setContactRoles(contactRoles));
  };
}

export function deleteContactRole(contactRole) {
  return changeContactRole(contactRole, { _destroy: true });
}

export const UPDATE_CONTACT = 'UPDATE_CONTACT';
export function updateContact(contact) {
  return {
    type: UPDATE_CONTACT,
    contact
  };
}

export function createCommission(attributes) {
  return async (dispatch, getState) => {
    const { deal, options } = getState();

    addPrimaryPlanToCommissionChanges(attributes, options.users);

    if (deal.hasTransactionSides) {
      attributes = handleCommissionTransactionSideDetails(deal, attributes);
    }

    const updatedDeal = await put(
      deal.path,
      serializeCommissions({ hasTransactionSides: deal.hasTransactionSides, commissions: [attributes] })
    );

    dispatch(setDeal(updatedDeal));
  };
}

function handleCommissionTransactionSideDetails(deal, commission) {
  const existingCommissionForPayee = deal.commissions.find((existingCommission) => {
    return hasMatchingPayee({ source: commission, target: existingCommission });
  });

  if (existingCommissionForPayee) {
    commission = { ...existingCommissionForPayee };
    setTransactionSideToDual(commission);
  } else {
    commission.transactionSideDetails = [newTransactionSideDetails(commission.transactionSide)];
  }

  return commission;
}

export function deleteCommission(commission) {
  return async (dispatch, getState) => {
    const savedDeal = await put(
      getState().deal.path,
      serializeCommissions({ commissions: [{ ...commission, _destroy: true }] })
    );

    dispatch(setDeal(savedDeal));
  };
}

export const SET_TRANSACTION_SIDE = 'SET_TRANSACTION_SIDE';
export function setTransactionSide(transactionSide) {
  return {
    type: SET_TRANSACTION_SIDE,
    transactionSide
  };
}

export function copyAttachments(attachmentIds) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const state = getState();

      dispatch(changeDeal({ attachmentsSaving: true, attachmentErrors: null, dirty: false }));

      let index = 0;
      const saveAttachment = (attachmentId) => {
        return post(state.deal.copyAttachmentPath, { attachmentId }).then((attachment) => {
          dispatch(addAttachment(attachment));

          index++;
          if (index >= attachmentIds.length) {
            dispatch(changeDeal({ attachmentsSaving: false, dirty: false }));
            resolve();
          } else {
            saveAttachment(attachmentIds[index]);
          }
        }).catch((errors) => {
          dispatch(setAttachmentErrors(errors));
          reject();
        });
      };

      if (attachmentIds && attachmentIds.length > 0) {
        saveAttachment(attachmentIds[0]);
      } else {
        dispatch(changeDeal({ attachmentsSaving: false, dirty: false }));
        resolve();
      }
    });
  };
}

function setAttachmentErrors(attachmentErrors) {
  return {
    type: CHANGE_DEAL,
    changes: { attachmentErrors, attachmentsSaving: false }
  };
}

export function updateThumbnail(uploadId) {
  return async (dispatch, getState) => {
    const deal = await post(
      getState().paths.updateThumbnail,
      { uploadId }
    );

    dispatch(setDeal(deal));
  };
}

export function connectProperty(researchPropertyId = null) {
  return async (dispatch, getState) => {
    const deal = await put(
      getState().deal.path,
      { researchPropertyId }
    );

    dispatch(setDeal(deal));
  };
}
